import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  RoutePlanApiError,
  routePlansAPI,
  type ComputeLegsPayload,
  type RoutePlanLeg,
} from '@/services/api/routePlans';

/**
 * useRoutePlanLegs — the server-computed leg km behind the `RoutePlanBuilder`
 * (CR2-D3). The browser never measures a distance: every change to the located
 * stop chain is debounced and sent to `POST /v1/route-plans/legs`; the previous
 * request is aborted and a monotonic sequence number drops any answer that
 * lands out of order, so an older set of legs can never overwrite a newer one.
 *
 * Precedence (lane-on-coordinate-match → Directions → haversine) lives on the
 * server; the builder only reads `source` back to badge each leg and keeps every
 * km editable (an edited km is never overwritten — see `applyServerLegs`).
 */

export type RoutePlanLegsStatus = 'idle' | 'pending' | 'ok' | 'error';

export interface UseRoutePlanLegsResult {
  status: RoutePlanLegsStatus;
  /** Legs for the CURRENT payload; null until the first answer lands. */
  legs: RoutePlanLeg[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const LEGS_DEBOUNCE_MS = 300;

export function useRoutePlanLegs(
  payload: ComputeLegsPayload | null,
  { enabled = true }: { enabled?: boolean } = {}
): UseRoutePlanLegsResult {
  const [status, setStatus] = useState<RoutePlanLegsStatus>('idle');
  const [legs, setLegs] = useState<RoutePlanLeg[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const seqRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const key = useMemo(
    () => (payload && enabled ? JSON.stringify(payload) : null),
    [payload, enabled]
  );

  const fire = useCallback(() => {
    const current = payloadRef.current;
    if (!current) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const seq = ++seqRef.current;
    setStatus('pending');
    setError(null);
    routePlansAPI
      .computeLegs(current, controller.signal)
      .then((res) => {
        if (seq !== seqRef.current) return; // superseded — drop
        setLegs(res);
        setStatus('ok');
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        if (err instanceof RoutePlanApiError && err.aborted) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Perhitungan jarak gagal');
      });
  }, []);

  useEffect(() => {
    if (key === null) {
      controllerRef.current?.abort();
      seqRef.current++;
      setStatus('idle');
      return;
    }
    const timer = setTimeout(fire, LEGS_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key, fire]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const refresh = useCallback(() => {
    if (!payloadRef.current) return;
    fire();
  }, [fire]);

  return {
    status,
    legs,
    loading: status === 'pending',
    error,
    refresh,
  };
}
